import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  FormHelperText,
  FormControl,
  RadioGroup,
  FormLabel,
  FormControlLabel,
  Radio
} from '@mui/material';

import config from '../../js/config';
import { getBearerString } from '../../js/jwt_token';


type LFDFilterDialogProps = {
  open: boolean;
  onClose: () => void;
  onSaveCallback: (requestBody: any) => void;
  searchParams?: URLSearchParams;
}

const LFDsFilterDialog = ({ open, onClose, onSaveCallback, searchParams }: LFDFilterDialogProps) => {
  const filterDefaultValues = {
    run_id: [],
    operator: [],
    robot: [],
    task_id: [],
    topics: [],
    task_successful: '', // '' means unset, 'true' or 'false' for selected
    session_log_type: "",
    pii_reviewed: '',
    pii_approved: '',
    data_transferred: '',
  };

  const [distinctValues, setDistinctValues] = useState(null);
  const [filters, setFilters] = useState(filterDefaultValues);

  // Helper to convert URLSearchParams to filter state
  function paramsToFilters(params: URLSearchParams) {
    return {
      run_id: params.getAll('run_id'),
      operator: params.getAll('operator'),
      robot: params.getAll('robot'),
      task_id: params.getAll('task_id'),
      topics: params.getAll('topics'),
      task_successful: params.get('task_successful') || '',
      session_log_type: params.get('session_log_type') || '',
      pii_reviewed: params.get('pii_reviewed') || '',
      pii_approved: params.get('pii_approved') || '',
      data_transferred: params.get('data_transferred') || '',
    };
  }

  // When dialog opens, initialize filters from searchParams if provided
  useEffect(() => {
    if (open) {
      if (searchParams) {
        setFilters(paramsToFilters(searchParams));
      } else {
        setFilters(filterDefaultValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchParams]);

  useEffect(() => {
    // Fetch distinct values only once
    const fetchDistinctValues = async () => {
      try {
        const response = await fetch(config.data_platform_api_url + '/lfds/filter_options', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': getBearerString()
          }
        });
        const data = await response.json();
        console.log(data);
        setDistinctValues(data);
      } catch (error) {
        console.error('Failed to fetch distinct values:', error);
      }
    };

    if (open && !distinctValues) {
      fetchDistinctValues();
    }
  }, [open, distinctValues]);


  const handleFilterChange = (field, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleReset = () => {

    // Reset the Values
    setFilters(filterDefaultValues);

    // Send the Reset Values (empty dict)
    onSaveCallback({});
    return
  };

  const onSave = () => {
    // Return Empty Body if equal to default values (see js/query_logs.js) to determine to query w/o filters
    if (JSON.stringify(filterDefaultValues) === JSON.stringify(filters)) {
      onSaveCallback({});
      return
    }

    // Else Return Filters
    var ret_filters = {
      ...filters,
    }

    onSaveCallback(ret_filters);
    return;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
    >
      <DialogTitle>Set LFD Filters</DialogTitle>
      <DialogContent>
        {distinctValues ? (
          <>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.run_id.sort((a, b) => a.localeCompare(b))} // Inline sorting
                value={filters.run_id}
                onChange={(event, value) => handleFilterChange('run_id', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Run ID"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>Select multiple Run IDs or start typing to filter the options.</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.operator.sort((a, b) => a.localeCompare(b))} // Inline sorting
                value={filters.operator}
                onChange={(event, value) => handleFilterChange('operator', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Operator"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>Select multiple Operators or start typing to filter the options.</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.robot.sort((a, b) => a.localeCompare(b))} // Inline sorting
                value={filters.robot}
                onChange={(event, value) => handleFilterChange('robot', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Robot"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>Select multiple Robots or start typing to filter the options.</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.task_id.sort((a, b) => a.localeCompare(b))} // Inline sorting
                value={filters.task_id}
                onChange={(event, value) => handleFilterChange('task_id', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Task Id"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>Select multiple Task Ids or start typing to filter the options.</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.topics.sort((a, b) => a.localeCompare(b))} // Inline sorting
                value={filters.topics}
                onChange={(event, value) => handleFilterChange('topics', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Topics"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>Select multiple Topics in the Data Struct or start typing to filter the options.</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                options={distinctValues.session_log_type.sort((a, b) => a.localeCompare(b))} // Inline sorting
                value={filters.session_log_type}
                onChange={(event, value) => handleFilterChange('session_log_type', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Session log Type"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>Select Session Log Type or start typing to filter the options.</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <FormLabel>Task Completion</FormLabel>
              <RadioGroup
                row
                value={filters.task_successful}
                onChange={(_, value) => handleFilterChange('task_successful', value)}
              >
                <FormControlLabel value="true" control={<Radio />} label="Successful" />
                <FormControlLabel value="false" control={<Radio />} label="Failed" />
                <FormControlLabel value="" control={<Radio />} label="Ignore" />
              </RadioGroup>
              <FormHelperText>Select task completion status (optional).</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <FormLabel>PII Reviewed</FormLabel>
              <RadioGroup
                row
                value={filters.pii_reviewed}
                onChange={(_, value) => handleFilterChange('pii_reviewed', value)}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
                <FormControlLabel value="" control={<Radio />} label="Ignore" />
              </RadioGroup>
              <FormHelperText>Filter by PII review status (optional).</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <FormLabel>PII Approved</FormLabel>
              <RadioGroup
                row
                value={filters.pii_approved}
                onChange={(_, value) => handleFilterChange('pii_approved', value)}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
                <FormControlLabel value="" control={<Radio />} label="Ignore" />
              </RadioGroup>
              <FormHelperText>Filter by PII approval status (optional).</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <FormLabel>Data Transferred</FormLabel>
              <RadioGroup
                row
                value={filters.data_transferred}
                onChange={(_, value) => handleFilterChange('data_transferred', value)}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
                <FormControlLabel value="" control={<Radio />} label="Ignore" />
              </RadioGroup>
              <FormHelperText>Filter by data transfer status (optional).</FormHelperText>
            </FormControl>
          </>
        ) : (
          <DialogContentText>Loading filter options...</DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSave} color="primary">
          Apply
        </Button>
        <Button onClick={handleReset} color="primary">
          Reset
        </Button>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LFDsFilterDialog;